"use client";

import { classed } from "@trydig/ui";
import { addLeadingSlash } from "@trydig/utils";
import Image from "next/image";
import { Translate } from "@/components/Translate";
import { Skeleton } from "@/components/ui/Skeleton";
import { ProductRating } from "@/features/reviews";
import { generateVariantReviewsIdentifiers } from "@/features/reviews/utils";
import { Link } from "@/i18n/routing";
import type { ProductCard as ProductCardType } from "@/lib/centra";
import { formatProductPrice } from "@/lib/utils";
import { useIsPreorder } from "../hooks";
import { isProductBundle } from "../utils";
import styles from "./ProductCard.module.css";

export interface ProductCardProps {
	product: ProductCardType;
	sizes: string;
	priority?: boolean;
	/**
	 * The previous path for the current product
	 */
	slug?: string;
}

const ProductCardTags = classed.div(styles.tags);
const ProductTag = classed.span(styles.tag);

const Price = ({
	price,
	priceBeforeDiscount,
}: {
	price?: string;
	priceBeforeDiscount?: string;
}) => {
	if (price !== priceBeforeDiscount) {
		return (
			<div className={styles.price}>
				<span className={styles.afterPrice}>{formatProductPrice(price)}</span>{" "}
				<span className={styles.beforePrice}>
					{formatProductPrice(priceBeforeDiscount)}
				</span>
			</div>
		);
	}
	if (price) {
		return <p className={styles.price}>{formatProductPrice(price)}</p>;
	}
	return null;
};

export const ProductCard = ({
	product,
	sizes,
	priority,
	slug,
	...props
}: ProductCardProps) => {
	const media = product.media.at(0);
	const link = addLeadingSlash(
		slug ? `/${slug}/${product.uri}` : `/${product.uri}`,
	);
	const variantCount = Array.isArray(product.variants)
		? product.variants.length
		: 0;

	const additionalColors = Math.max(0, variantCount);

	const isBundle = isProductBundle(product);

	const { data: preorderData } = useIsPreorder({
		centraVariantId: product?.centraVariant,
		isBundle,
		displayItemId: product.displayItemId,
	});

	return (
		<article className={styles.root} {...props}>
			<ProductCardTags>
				{preorderData && !product.outOfStock && (
					<ProductTag>
						<Translate>Pre-order</Translate>
					</ProductTag>
				)}
				{product.outOfStock && !preorderData && (
					<ProductTag>
						<Translate>Coming soon</Translate>
					</ProductTag>
				)}
			</ProductCardTags>

			<Link href={link} aria-label={`View details about ${product.name}`}>
				<figure className={styles.image}>
					<Image
						priority={priority}
						sizes={sizes}
						src={media?.href || "/fallback-image.jpg"}
						alt={media?.alt || ""}
						fill
					/>
				</figure>
			</Link>

			<hgroup className={styles.info}>
				<div className={styles.productName}>
					<span className={styles.title}>
						<Link href={link} aria-label={`View details about ${product.name}`}>
							{product.name}
						</Link>
						<ProductRating
							productIds={generateVariantReviewsIdentifiers(product)}
						/>
					</span>
					<span className={styles.cutline}>{product.variantName}</span>
				</div>
				<Price
					price={product.price}
					priceBeforeDiscount={product.priceBeforeDiscount}
				/>
				{additionalColors > 0 && (
					<span className={styles.moreVariants}>
						+{additionalColors}{" "}
						<Translate>
							{additionalColors > 1 ? "more colors" : "more color"}
						</Translate>
					</span>
				)}
			</hgroup>
		</article>
	);
};

export const ProductCardSkeleton = ({ hideColors = false }) => {
	return (
		<article className={styles.root}>
			<Skeleton className={styles.image} />
			<hgroup className={styles.info}>
				<div className={styles.productName}>
					<span className={styles.title}>
						<Skeleton fitContent style={{ borderRadius: "0px" }}>
							<span>lorem ipsum</span>
						</Skeleton>
					</span>
					<Skeleton fitContent style={{ borderRadius: "0px" }}>
						<span className={styles.cutline}>lorem</span>
					</Skeleton>
				</div>
				<Skeleton fitContent style={{ borderRadius: "0px" }}>
					<p className={styles.price}>lorem ipsum</p>
				</Skeleton>
				{!hideColors && (
					<Skeleton fitContent style={{ borderRadius: "0px" }}>
						<span className={styles.moreVariants}>+1 more color</span>
					</Skeleton>
				)}
			</hgroup>
		</article>
	);
};
